import classNames from "classnames";
import * as React from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { ITestRunnerMethod, TestRunSide, TestRunStatus } from "./test-runner.types";

interface TestRunnerMethodProps {
  method: ITestRunnerMethod;
  isSelected: boolean;
  output: string;
  running: boolean;
  runningSide: TestRunSide | null;
  showServerClientSplit: boolean;
  runButtonLabel: string;
  paramValue: (methodName: string, paramName: string) => string;
  onSelect: (name: string) => void;
  onParamChange: (methodName: string, paramName: string, value: string) => void;
  onRun: (serverSide: boolean) => void;
}

const placeholderFor = (type: string): string => {
  if (type === "number") return "0";
  if (type === "boolean") return "false";
  return "value";
};

interface SignatureProps {
  method: ITestRunnerMethod;
  status: TestRunStatus;
}

const Signature: React.FC<SignatureProps> = ({ method, status }) => {
  const params: React.ReactNode[] = [];
  method.parameters.forEach((p, index) => {
    if (index > 0) {
      params.push(
        <span key={`sep-${index}`} className="kuika-test-runner__sig-punct">
          ,{" "}
        </span>
      );
    }
    params.push(
      <span key={p.name}>
        <span className="kuika-test-runner__sig-param">{p.name}</span>
        <span className="kuika-test-runner__sig-punct">: </span>
        <span className="kuika-test-runner__sig-type">{p.type}</span>
      </span>
    );
  });

  return (
    <div className="kuika-test-runner__signature">
      <span className={classNames("kuika-test-runner__status", `kuika-test-runner__status--${status}`)} />
      <span className="kuika-test-runner__sig-name">{method.name}</span>
      <span className="kuika-test-runner__sig-punct">(</span>
      {params}
      <span className="kuika-test-runner__sig-punct">)</span>
      <span className="kuika-test-runner__sig-punct">: </span>
      <span className="kuika-test-runner__sig-type">{method.returnType}</span>
    </div>
  );
};

interface RunButtonProps {
  label: string;
  primary: boolean;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}

const RunButton: React.FC<RunButtonProps> = ({ label, primary, loading, disabled, onClick }) => (
  <KuikaButton
    type={primary ? "primary" : "secondary"}
    size="sm"
    loading={loading}
    disabled={disabled}
    onClick={(e?: React.MouseEvent) => {
      e?.stopPropagation();
      onClick();
    }}
  >
    <span className="kuika-test-runner__run">
      <KuikaSvg name="player-play" width={12} height={12} className="kuika-test-runner__run-icon" />
      {label}
    </span>
  </KuikaButton>
);

const TestRunnerMethod: React.FC<TestRunnerMethodProps> = ({
  method,
  isSelected,
  output,
  running,
  runningSide,
  showServerClientSplit,
  runButtonLabel,
  paramValue,
  onSelect,
  onParamChange,
  onRun
}) => {
  const isError: boolean = isSelected && output.startsWith("Error");
  const hasOutput: boolean = isSelected && output.length > 0;

  let status: TestRunStatus = "idle";
  if (isSelected && running) status = "running";
  else if (isError) status = "error";
  else if (hasOutput) status = "success";

  return (
    <div
      className={classNames("kuika-test-runner__row", { "kuika-test-runner__row--selected": isSelected })}
      onClick={() => onSelect(method.name)}
    >
      <Signature method={method} status={status} />

      {isSelected && (
        <div className="kuika-test-runner__detail">
          {method.parameters.length > 0 && (
            <div className="kuika-test-runner__params">
              {method.parameters.map((p) => (
                <label key={p.name} className="kuika-test-runner__param">
                  <span className="kuika-test-runner__param-label">
                    {p.name}
                    <span className="kuika-test-runner__sig-punct"> {p.type}</span>
                  </span>
                  <KuikaInput
                    size="sm"
                    className="kuika-test-runner__param-input"
                    placeholder={placeholderFor(p.type)}
                    value={paramValue(method.name, p.name)}
                    onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                      onParamChange(method.name, p.name, String(value))
                    }
                    onPressEnter={() => onRun(true)}
                  />
                </label>
              ))}
            </div>
          )}

          <div className="kuika-test-runner__actions">
            {showServerClientSplit ? (
              <>
                <RunButton
                  label="Server"
                  primary
                  loading={running && runningSide === "server"}
                  disabled={running && runningSide !== "server"}
                  onClick={() => onRun(true)}
                />
                <RunButton
                  label="Client"
                  primary={false}
                  loading={running && runningSide === "client"}
                  disabled={running && runningSide !== "client"}
                  onClick={() => onRun(false)}
                />
              </>
            ) : (
              <RunButton
                label={runButtonLabel}
                primary
                loading={running}
                disabled={false}
                onClick={() => onRun(true)}
              />
            )}
          </div>

          {hasOutput && !running && (
            <pre
              className={classNames("kuika-test-runner__output", {
                "kuika-test-runner__output--error": isError,
                "kuika-test-runner__output--success": !isError
              })}
            >
              {output}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default TestRunnerMethod;
